import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createVNode as _createVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, withKeys as _withKeys, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "relative w-full" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 3 }
const _hoisted_4 = ["aria-expanded"]
const _hoisted_5 = {
  class: "material-icons",
  style: {"color":"#fff","vertical-align":"middle"}
}
const _hoisted_6 = ["id", "href"]
const _hoisted_7 = ["id", "onClick"]
const _hoisted_8 = ["id", "href"]
const _hoisted_9 = { class: "material-icons text-[18px] text-neutral-90" }
const _hoisted_10 = ["href"]
const _hoisted_11 = ["href"]
const _hoisted_12 = { class: "px-4 py-2 text-xs text-neutral-80 break-all" }

import { computed, defineAsyncComponent, nextTick, ref } from 'vue'
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core'

// stores
import { useAuthStore } from '@shared/stores/auth'
import useTopAppBarItemsStore from '@shared/stores/top-app-bar'
import { useRoute, useRouter } from 'vue-router'
import { useUserInfoStore } from '@shared/stores/user-info'
import { supportArticles } from '@shared/constants/SupportArticles'
import { BaseRouteNames as Routes } from '@/router/routes'

// design system
const switchInstances: Routes = 'SwitchInstances'
//@ts-expect-error because these are dynamic route names
const subscriptionManagementName: Routes = 'SubscriptionManagement'
const homeName: Routes = 'Home'


export default /*@__PURE__*/_defineComponent({
  __name: 'TheAppBar',
  emits: ["onLogout"],
  setup(__props, { emit: __emit }) {

const TopAppBar = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/TopAppBar.vue'))
const UpdatedTopAppBar = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/UpdatedTopAppBar.vue'))
const UpdatedTopAppBarMenuItem = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/UpdatedTopAppBarMenuItem.vue'))
const TopAppBarMenuItem = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/TopAppBarMenuItem.vue'))
const TopAppBarMenuItemDropdownDivider = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/TopAppBarMenuItemDropdownDivider.vue'))
const TopAppBarMenuItemDropdownLink = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/TopAppBarMenuItemDropdownLink.vue'))
const TopAppBarMenuItemDropdownText = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/TopAppBarMenuItemDropdownText.vue'))

const authStore = useAuthStore()
const topAppBarItemsStore = useTopAppBarItemsStore()
topAppBarItemsStore.update()
const userInfoStore = useUserInfoStore()
userInfoStore.update()
const router = useRouter()
const route = useRoute()

const breakpoints = useBreakpoints(breakpointsTailwind)
// UpdatedTopAppBar hides nav-tabs below xl (1280px); legacy TopAppBar hides
// the entire right-side area (slot + profile dropdown) below lg (1024px).
// Match the active version so the hamburger appears exactly when the tabs disappear.
const isXl = breakpoints.greaterOrEqual('xl')
const isLg = breakpoints.greaterOrEqual('lg')
const showHamburger = computed(() => topAppBarItemsStore.updatedVersion ? !isXl.value : !isLg.value)

// Where to place the hamburger horizontally. On UpdatedTopAppBar the profile
// dropdown is visible to its right at all widths, so we leave room for it.
// On legacy TopAppBar the profile dropdown is hidden when the hamburger shows,
// so we put it at the far right.
const hamburgerRightOffset = computed(() =>
  topAppBarItemsStore.updatedVersion ? '13rem' : '1rem'
)

const isMobileMenuOpen = ref(false)
const hamburgerRef = ref<HTMLButtonElement | null>(null)
const mobileMenuRef = ref<HTMLElement | null>(null)

const dropdownStyle = computed(() => {
  if (!hamburgerRef.value) return { top: '64px', right: '0px' }
  const rect = hamburgerRef.value.getBoundingClientRect()
  return {
    top: `${Math.round(rect.bottom) + 4}px`,
    right: `${Math.round(window.innerWidth - rect.right)}px`,
  }
})

function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  if (isMobileMenuOpen.value) {
    nextTick(() => {
      mobileMenuRef.value?.querySelector<HTMLElement>('a, button')?.focus()
    })
  } else {
    hamburgerRef.value?.focus()
  }
}

function closeMobileMenu() {
  isMobileMenuOpen.value = false
  hamburgerRef.value?.focus()
}

function navigateInternal(routeName: string | undefined) {
  if (!routeName) {
    return
  }
  router.push({ name: routeName })
  closeMobileMenu()
}

const showSettingsMenuItem = computed(() => !route.matched.some(({ name }) => name === subscriptionManagementName || name === homeName) && topAppBarItemsStore.userSettingsRoute !== undefined)
const showVersionMenuItem = computed(() => false)
const showSwitchInstancesMenuItem = computed(() => !route.matched.some(({ name }) => name === switchInstances) && !isUserSingleClientOrInstance.value)

const isUserSingleClientOrInstance = computed(() => userInfoStore.clients.length === 1 && userInfoStore.instances.length <= 1)

// The legacy TopAppBar wraps its profile dropdown in a container that's hidden below `lg`,
// so when the burger is shown on the legacy version the user otherwise loses access to
// Logout / Switch instance / support links. Mirror those into the burger in that case.
// On UpdatedTopAppBar the profile dropdown stays visible regardless of viewport, so we
// don't duplicate.
const includeProfileItemsInBurger = computed(() => !topAppBarItemsStore.updatedVersion && showHamburger.value)

const emit = __emit

function onLogout() {
  emit('onLogout')
}

function onLogoutFromBurger() {
  closeMobileMenu()
  emit('onLogout')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (_unref(topAppBarItemsStore).updatedVersion)
        ? (_openBlock(), _createBlock(_unref(UpdatedTopAppBar), {
            key: 0,
            "user-name": _unref(authStore).username,
            "menu-items": showHamburger.value ? [] : _unref(topAppBarItemsStore).menuItems
          }, {
            "profile-dropdown": _withCtx(() => [
              _createVNode(_unref(TopAppBarMenuItemDropdownLink), {
                id: "top_appbar_dropdown_username_logout",
                text: "Logout",
                onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (onLogout()), ["prevent"]))
              }),
              (showSettingsMenuItem.value)
                ? (_openBlock(), _createBlock(_unref(TopAppBarMenuItemDropdownLink), {
                    key: 0,
                    id: "top_appbar_dropdown_username_settings",
                    text: "Settings",
                    route: _unref(topAppBarItemsStore).userSettingsRoute,
                    external: true,
                    target: "_self"
                  }, null, 8 /* PROPS */, ["route"]))
                : _createCommentVNode("v-if", true),
              _createVNode(_unref(TopAppBarMenuItemDropdownDivider)),
              _createVNode(_unref(TopAppBarMenuItemDropdownLink), {
                id: "top_appbar_dropdown_username_get_training",
                text: "Get training",
                route: "https://skynamo.com/training/",
                external: true
              }),
              _createVNode(_unref(TopAppBarMenuItemDropdownLink), {
                id: "top_appbar_dropdown_username_help",
                text: "Support desk",
                route: _unref(supportArticles).supportDesk,
                external: true
              }, null, 8 /* PROPS */, ["route"]),
              _createVNode(_unref(TopAppBarMenuItemDropdownLink), {
                id: "top_appbar_dropdown_username_feedback",
                text: "Add an idea",
                route: "https://portal.skynamo.com/",
                external: true
              }),
              _createVNode(_unref(TopAppBarMenuItemDropdownDivider)),
              _createVNode(_unref(TopAppBarMenuItemDropdownText), {
                id: "top_appbar_dropdown_username_organisation",
                heading: "Organisation",
                text: _unref(authStore).instanceName
              }, null, 8 /* PROPS */, ["text"]),
              _createVNode(_unref(TopAppBarMenuItemDropdownDivider)),
              (showVersionMenuItem.value)
                ? (_openBlock(), _createBlock(_unref(TopAppBarMenuItemDropdownText), {
                    key: 1,
                    id: "top_appbar_dropdown_username_skynamo_version",
                    text: "Skynamo 2.0"
                  }))
                : _createCommentVNode("v-if", true)
            ]),
            default: _withCtx(() => [
              (_unref(topAppBarItemsStore).settingsMenuItem !== undefined && !showHamburger.value)
                ? (_openBlock(), _createBlock(_unref(UpdatedTopAppBarMenuItem), {
                    key: 0,
                    id: _unref(topAppBarItemsStore).settingsMenuItem.id,
                    "icon-name": _unref(topAppBarItemsStore).settingsMenuItem?.iconName,
                    external: _unref(topAppBarItemsStore).settingsMenuItem?.external,
                    route: _unref(topAppBarItemsStore).settingsMenuItem?.route
                  }, null, 8 /* PROPS */, ["id", "icon-name", "external", "route"]))
                : _createCommentVNode("v-if", true)
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["user-name", "menu-items"]))
        : (_openBlock(), _createBlock(_unref(TopAppBar), {
            key: 1,
            name: _unref(authStore).instanceName ? _unref(authStore).instanceName : _unref(authStore).username,
            "menu-items": showHamburger.value ? [] : _unref(topAppBarItemsStore).menuItems
          }, {
            "profile-dropdown": _withCtx(() => [
              (showSettingsMenuItem.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createVNode(_unref(TopAppBarMenuItemDropdownLink), {
                      id: "top_appbar_dropdown_username_settings",
                      text: "Settings",
                      route: _unref(topAppBarItemsStore).userSettingsRoute,
                      external: true,
                      target: "_self"
                    }, null, 8 /* PROPS */, ["route"]),
                    _createVNode(_unref(TopAppBarMenuItemDropdownDivider))
                  ]))
                : _createCommentVNode("v-if", true),
              _createVNode(_unref(TopAppBarMenuItemDropdownLink), {
                id: "top_appbar_dropdown_username_get_training",
                text: "Get training",
                route: "https://skynamo.com/training/",
                "left-icon": "bolt",
                external: true
              }),
              _createVNode(_unref(TopAppBarMenuItemDropdownLink), {
                id: "top_appbar_dropdown_username_help",
                text: "Support desk",
                route: _unref(supportArticles).supportDesk,
                "left-icon": "contact_support",
                external: true
              }, null, 8 /* PROPS */, ["route"]),
              _createVNode(_unref(TopAppBarMenuItemDropdownLink), {
                id: "top_appbar_dropdown_username_feedback",
                text: "Add an idea",
                route: "https://portal.skynamo.com/",
                "left-icon": "lightbulb",
                external: true
              }),
              (showSwitchInstancesMenuItem.value)
                ? (_openBlock(), _createBlock(_unref(TopAppBarMenuItemDropdownDivider), { key: 1 }))
                : _createCommentVNode("v-if", true),
              (showSwitchInstancesMenuItem.value)
                ? (_openBlock(), _createBlock(_unref(TopAppBarMenuItemDropdownLink), {
                    key: 2,
                    id: "top_appbar_dropdown_username_switch_instances",
                    text: "Switch instance",
                    route: "SwitchInstances",
                    "left-icon": "restart_alt",
                    external: false
                  }))
                : _createCommentVNode("v-if", true),
              _createVNode(_unref(TopAppBarMenuItemDropdownDivider)),
              _createVNode(_unref(TopAppBarMenuItemDropdownText), {
                id: "top_appbar_dropdown_username_email",
                class: "pl-[15px] pr-6",
                text: _unref(authStore).email
              }, null, 8 /* PROPS */, ["text"]),
              _createVNode(_unref(TopAppBarMenuItemDropdownText), {
                id: "top_appbar_dropdown_username_logout",
                class: "cursor-pointer hover:bg-neutral-10 pl-[19px]",
                text: "Log out",
                "left-icon": "logout",
                onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (onLogout()), ["prevent"]))
              }),
              (showVersionMenuItem.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createVNode(_unref(TopAppBarMenuItemDropdownDivider)),
                    _createVNode(_unref(TopAppBarMenuItemDropdownText), {
                      id: "top_appbar_dropdown_username_skynamo_version",
                      text: "Skynamo 2.0"
                    })
                  ]))
                : _createCommentVNode("v-if", true)
            ]),
            default: _withCtx(() => [
              (_unref(topAppBarItemsStore).settingsMenuItem !== undefined && !showHamburger.value)
                ? (_openBlock(), _createBlock(_unref(TopAppBarMenuItem), {
                    key: 0,
                    id: _unref(topAppBarItemsStore).settingsMenuItem.id,
                    "icon-name": _unref(topAppBarItemsStore).settingsMenuItem?.iconName,
                    "icon-size": "18",
                    external: _unref(topAppBarItemsStore).settingsMenuItem?.external,
                    route: _unref(topAppBarItemsStore).settingsMenuItem?.route
                  }, null, 8 /* PROPS */, ["id", "icon-name", "external", "route"]))
                : _createCommentVNode("v-if", true)
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["name", "menu-items"])),
      _createCommentVNode(" Hamburger overlay — rendered outside the design-system component so it works on both\n       UpdatedTopAppBar (slot always visible) AND legacy TopAppBar (right side hidden below lg). "),
      (showHamburger.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 2,
            ref_key: "hamburgerRef",
            ref: hamburgerRef,
            class: "absolute top-0 z-[60] h-full flex items-center justify-center cursor-pointer hover:bg-shade-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-shade-white/50",
            style: _normalizeStyle({ right: hamburgerRightOffset.value, padding: '0 14px' }),
            "aria-label": "Open navigation",
            "aria-expanded": isMobileMenuOpen.value,
            onClick: toggleMobileMenu
          }, [
            _createElementVNode("span", _hoisted_5, _toDisplayString(isMobileMenuOpen.value ? 'close' : 'menu'), 1 /* TEXT */)
          ], 12 /* STYLE, PROPS */, _hoisted_4))
        : _createCommentVNode("v-if", true)
    ]),
    _createCommentVNode(" Mobile nav dropdown — rendered outside the app bar to avoid stacking-context clipping "),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isMobileMenuOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed inset-0 z-40",
            onClick: closeMobileMenu
          }))
        : _createCommentVNode("v-if", true),
      (isMobileMenuOpen.value)
        ? (_openBlock(), _createElementBlock("nav", {
            key: 1,
            ref_key: "mobileMenuRef",
            ref: mobileMenuRef,
            class: "fixed z-50 min-w-[240px] max-w-[320px] bg-shade-white border border-neutral-40 rounded-lg shadow-lg py-1 max-h-[80vh] overflow-y-auto",
            style: _normalizeStyle(dropdownStyle.value),
            "aria-label": "Navigation menu",
            onKeydown: _withKeys(closeMobileMenu, ["esc"])
          }, [
            _createCommentVNode(" Main nav items "),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(topAppBarItemsStore).menuItems, (item) => {
              return (_openBlock(), _createElementBlock(_Fragment, {
                key: item.id
              }, [
                (item.external)
                  ? (_openBlock(), _createElementBlock("a", {
                      key: 0,
                      id: `mobile_nav_${item.id}`,
                      href: item.route,
                      class: "block px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none",
                      onClick: closeMobileMenu
                    }, _toDisplayString(item.displayName), 9 /* TEXT, PROPS */, _hoisted_6))
                  : (_openBlock(), _createElementBlock("button", {
                      key: 1,
                      id: `mobile_nav_${item.id}`,
                      class: "flex w-full items-center px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none",
                      onClick: ($event: any) => (navigateInternal(item.route))
                    }, _toDisplayString(item.displayName), 9 /* TEXT, PROPS */, _hoisted_7))
              ], 64 /* STABLE_FRAGMENT */))
            }), 128 /* KEYED_FRAGMENT */)),
            _createCommentVNode(" Settings icon item (links to device/warehouse management on the instance) "),
            (_unref(topAppBarItemsStore).settingsMenuItem !== undefined)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _cache[4] || (_cache[4] = _createElementVNode("div", {
                    class: "my-1 border-t border-neutral-30",
                    role: "separator"
                  }, null, -1 /* CACHED */)),
                  _createElementVNode("a", {
                    id: `mobile_nav_${_unref(topAppBarItemsStore).settingsMenuItem.id}`,
                    href: _unref(topAppBarItemsStore).settingsMenuItem.route,
                    class: "flex items-center gap-3 px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none",
                    onClick: closeMobileMenu
                  }, [
                    _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(topAppBarItemsStore).settingsMenuItem.iconName), 1 /* TEXT */),
                    _cache[3] || (_cache[3] = _createElementVNode("span", null, "Settings", -1 /* CACHED */))
                  ], 8 /* PROPS */, _hoisted_8)
                ], 64 /* STABLE_FRAGMENT */))
              : _createCommentVNode("v-if", true),
            _createCommentVNode(" Profile items — included only when the design-system component hides the\n           profile dropdown (legacy TopAppBar below lg). On UpdatedTopAppBar the\n           profile dropdown stays visible to the right of the hamburger, so we\n           don't duplicate here. "),
            (includeProfileItemsInBurger.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _cache[5] || (_cache[5] = _createElementVNode("div", {
                    class: "my-1 border-t border-neutral-30",
                    role: "separator"
                  }, null, -1 /* CACHED */)),
                  (showSettingsMenuItem.value)
                    ? (_openBlock(), _createElementBlock("a", {
                        key: 0,
                        id: "mobile_nav_user_settings",
                        href: _unref(topAppBarItemsStore).userSettingsRoute,
                        class: "block px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none",
                        onClick: closeMobileMenu
                      }, "User settings", 8 /* PROPS */, _hoisted_10))
                    : _createCommentVNode("v-if", true),
                  _createElementVNode("a", {
                    id: "mobile_nav_get_training",
                    href: "https://skynamo.com/training/",
                    target: "_blank",
                    rel: "noopener",
                    class: "block px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none",
                    onClick: closeMobileMenu
                  }, "Get training"),
                  _createElementVNode("a", {
                    id: "mobile_nav_support_desk",
                    href: _unref(supportArticles).supportDesk,
                    target: "_blank",
                    rel: "noopener",
                    class: "block px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none",
                    onClick: closeMobileMenu
                  }, "Support desk", 8 /* PROPS */, _hoisted_11),
                  _createElementVNode("a", {
                    id: "mobile_nav_add_idea",
                    href: "https://portal.skynamo.com/",
                    target: "_blank",
                    rel: "noopener",
                    class: "block px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none",
                    onClick: closeMobileMenu
                  }, "Add an idea"),
                  (showSwitchInstancesMenuItem.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        id: "mobile_nav_switch_instances",
                        class: "flex w-full items-center px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (navigateInternal('SwitchInstances')))
                      }, " Switch instance "))
                    : _createCommentVNode("v-if", true),
                  _cache[6] || (_cache[6] = _createElementVNode("div", {
                    class: "my-1 border-t border-neutral-30",
                    role: "separator"
                  }, null, -1 /* CACHED */)),
                  _createElementVNode("div", _hoisted_12, _toDisplayString(_unref(authStore).email), 1 /* TEXT */),
                  _createElementVNode("button", {
                    id: "mobile_nav_logout",
                    class: "flex w-full items-center px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none",
                    onClick: onLogoutFromBurger
                  }, " Log out ")
                ], 64 /* STABLE_FRAGMENT */))
              : _createCommentVNode("v-if", true)
          ], 36 /* STYLE, NEED_HYDRATION */))
        : _createCommentVNode("v-if", true)
    ]))
  ], 64 /* STABLE_FRAGMENT */))
}
}

})